// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { menuBodyStyle } from "@stoneage/App/Components/Menu/DropdownMenu";
import { xBaseTheme } from "@stoneage/XDL/types";
import { css } from "styled-components";
import { autoClamp, color } from "@stoneage/XDL/Utilities";
import { fontConfig, HEADER_HEIGHT_CSS_VAR } from "@stoneage/XDL/constants";

const locationInfoWindowStyles = css`
    .gm-style-iw {
        box-shadow: 0 2px 4px 0 ${color("secondary", 0.1)} !important;
        border-radius: 0 !important;

        .gm-style-iw-chr {
            position: absolute;
            right: 5px;
            top: 8px;
            & > .gm-style-iw-ch {
                display: none;
            }
            & > button.gm-ui-hover-effect {
                width: 14px !important;
                height: 14px !important;
                & > span {
                    width: 14px !important;
                    height: 14px !important;
                    margin: 0 !important;
                }
            }
        }
    }
`;

// Marketo injects per-form custom CSS with hard-coded colors; align its controls with the DL brand color
const marketoFormStyles = css`
    .mktoForm {
        .mktoCheckboxList input + label:hover::before,
        .mktoRadioList input + label:hover::before {
            border-color: ${color("primary")} !important;
        }
        .mktoCheckboxList input:checked + label::before,
        .mktoRadioList input:checked + label::before {
            background-color: ${color("secondary")} !important;
            border-color: ${color("secondary")} !important;
        }
        .mktoCheckboxList input:checked + label:hover::before,
        .mktoRadioList input:checked + label:hover::before {
            background-color: ${color("primary")} !important;
            border-color: ${color("primary")} !important;
        }
        .mktoButtonWrap.mktoSimple button.mktoButton {
            background: ${color("primary")} !important;
            border: 1px solid ${color("primary")} !important;
            color: ${color("common.white")} !important;
            &:hover {
                background: ${color("common.white")} !important;
                color: ${color("primary")} !important;
            }
            &:focus,
            &:active {
                background: ${color("primary")} !important;
                color: ${color("common.white")} !important;
            }
        }
    }
`;

export const specialClassesStyles = css`
    // add general color classes
    .as-blue {
        color: ${color("primary")};
    }
    .as-yellow {
        color: ${color("secondary")};
    }
    .as-orange {
        color: ${color("common.ui8")};
    }
    .as-white {
        color: ${color("common.white")};
    }
    .special-h1 {
        color: ${color(fontConfig.headingColor)};
        font-size: ${autoClamp(36, 40, 44)};
        line-height: ${autoClamp(42, 48, 52)};
        font-weight: ${fontConfig.weight.black};
        font-family: ${fontConfig.fontFamily};
        margin-bottom: 20px;
        letter-spacing: 0;
        display: block;
    }
    .special-h2 {
        color: ${color(fontConfig.headingColor)};
        font-size: ${autoClamp(28, 32, 36)};
        line-height: ${autoClamp(33, 38, 42)};
        font-weight: ${fontConfig.weight.black};
        font-family: ${fontConfig.fontFamily};
        margin-bottom: 20px;
        letter-spacing: 0;
        display: block;
    }
`;

export default {
    css: css`
        :root {
            scroll-behavior: smooth;
            /* keeps anchor/scrollIntoView targets clear of the sticky header */
            scroll-padding-top: calc(var(${HEADER_HEIGHT_CSS_VAR}, 100px) + 20px);
        }

        *:focus {
            outline: none !important;
        }

        ul {
            margin: 0;
            padding: 0;
            list-style: inherit;
        }

        body {
            overflow-x: hidden;

            ._ls_sideBar {
                display: none;
            }

            @media print {
                font-size: 13px !important;
            }
        }

        .button-icon {
            padding: 0 24px;
            border-radius: 4px;
        }

        ${menuBodyStyle}
        ${locationInfoWindowStyles}
        ${specialClassesStyles}
        ${marketoFormStyles}
    `,
} as xBaseTheme["globalStyle"];
